'use client'

import Link from 'next/link'
import { useEffect, useMemo, useState, useTransition } from 'react'
import TeamLogo from '@/components/sport/TeamLogo'
import type {
  LivescoreMatch,
  LivescorePayload,
  LivescoreStage,
} from '@/lib/livescore-data'
import { getApiBaseUrl } from '@/lib/config'
import { leagueLogoSrc, matchesPath } from '@/lib/standings-logos'

type StageTab = 'all' | LivescoreStage

async function loadLivescore(
  lig?: string | null,
  day?: string | null,
): Promise<LivescorePayload | null> {
  try {
    const q = new URLSearchParams()
    if (lig) q.set('lig', lig)
    if (day) q.set('day', day)
    const qs = q.toString()
    const res = await fetch(
      `${getApiBaseUrl()}/api/livescore${qs ? `?${qs}` : ''}`,
      { cache: 'no-store' },
    )
    if (!res.ok) return null
    const json = await res.json()
    return json?.data ?? null
  } catch {
    return null
  }
}

function badgeFor(m: LivescoreMatch): { text: string; className: string } | null {
  const min = m.min.trim()
  const status = min.toLocaleUpperCase('tr-TR')

  if (m.stage === 'finished') {
    return {
      text: 'Bitti',
      className:
        'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300',
    }
  }

  if (
    m.stage === 'live' &&
    ['HT', 'İY', 'IY', 'UZ.İY', 'UZ.IY'].includes(status)
  ) {
    return {
      text: status.startsWith('UZ.') ? 'Uzatma Devre' : 'Devre',
      className:
        'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    }
  }

  if (m.stage === 'live') {
    return {
      text: /^\d+(?:\+\d+)?$/.test(min) ? `Canlı ${min}'` : 'Canlı',
      className:
        'livescore-live-label border border-red-100 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-950/40 dark:text-red-300',
    }
  }

  if (['ERT', 'ERTELENDİ', 'ERTELENDI'].includes(status)) {
    return {
      text: 'Ertelendi',
      className:
        'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
    }
  }

  if (['İPT', 'IPT', 'İPTAL', 'IPTAL'].includes(status)) {
    return {
      text: 'İptal',
      className:
        'bg-gray-100 text-gray-600 dark:bg-slate-800 dark:text-slate-300',
    }
  }

  // Yaklaşan: saat zaten `start` alanında — "Yakında" tekrarını gösterme
  if (m.stage === 'upcoming' && m.start?.trim()) {
    return null
  }

  return null
}

function subText(m: LivescoreMatch): string {
  const parts: string[] = []
  if (m.ht) parts.push(`İY ${m.ht.home}-${m.ht.away}`)
  if (m.pen) parts.push(`Pen ${m.pen.home}-${m.pen.away}`)
  if (m.agg) parts.push(`Toplam ${m.agg.home}-${m.agg.away}`)
  return parts.join(' · ')
}

export default function LivescoreBoard({
  initial,
  lig,
  day,
  displayDay,
  isToday = true,
}: {
  initial: LivescorePayload
  lig?: string | null
  /** API sorgusu için (bugün değilse YYYY-MM-DD) */
  day?: string | null
  /** Başlıkta gösterilecek gün */
  displayDay?: string | null
  isToday?: boolean
}) {
  const [data, setData] = useState(initial)
  const [stage, setStage] = useState<StageTab>('all')
  const [, startTransition] = useTransition()
  const labelDay = displayDay || day || data.day

  useEffect(() => {
    setData(initial)
  }, [initial])

  useEffect(() => {
    const hasLive = (initial.sections || []).some((s) =>
      (s.matches || []).some((m) => m.stage === 'live'),
    )
    setStage(hasLive ? 'live' : 'all')
  }, [initial.day, initial.leagueSlug, initial.sections])

  useEffect(() => {
    const tick = () => {
      startTransition(() => {
        void loadLivescore(lig, day).then((next) => {
          if (next) setData(next)
        })
      })
    }
    const id = window.setInterval(tick, 45_000)
    return () => window.clearInterval(id)
  }, [lig, day])

  const tabs: { key: StageTab; label: string }[] = [
    { key: 'all', label: 'Tümü' },
    { key: 'live', label: 'Canlı' },
    { key: 'finished', label: 'Bitti' },
    { key: 'upcoming', label: 'Yakında' },
  ]

  const visibleSections = useMemo(() => {
    return (data.sections || [])
      .map((sec) => ({
        ...sec,
        matches:
          stage === 'all'
            ? sec.matches || []
            : (sec.matches || []).filter((m) => m.stage === stage),
      }))
      .filter((sec) => sec.matches.length > 0)
  }, [data.sections, stage])

  return (
    <div className="livescore-panel space-y-3 sm:space-y-4">
      <div className="flex items-center justify-between rounded-xl border border-gray-100 bg-white px-3 py-2 dark:border-slate-800 dark:bg-slate-900 sm:hidden">
        <div className="flex min-w-0 items-center gap-2.5">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={leagueLogoSrc(data.leagueSlug || 'all')}
            alt=""
            width={30}
            height={30}
            className="size-[30px] shrink-0 object-contain"
          />
          <h2 className="truncate text-sm font-black text-gray-900 dark:text-white">
            {data.title}
          </h2>
        </div>
        <span className="shrink-0 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-bold tabular-nums text-gray-600 dark:bg-slate-800 dark:text-slate-300">
          {isToday ? 'Bugün' : labelDay}
        </span>
      </div>

      <div className="hidden flex-col items-center text-center sm:flex">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={leagueLogoSrc(data.leagueSlug || 'all')}
          alt=""
          width={72}
          height={72}
          className="size-[72px] object-contain"
        />
        <h2 className="mt-2.5 text-xl font-black tracking-wide text-gray-900 dark:text-white">
          {data.title}
        </h2>
        <p className="mt-1 text-sm font-bold tabular-nums text-gray-500 dark:text-slate-400">
          {isToday ? 'Bugün' : labelDay}
        </p>
      </div>

      <div
        className="grid grid-cols-4 gap-1.5 sm:flex sm:gap-2 sm:overflow-x-auto sm:pb-1 sm:[-ms-overflow-style:none] sm:[scrollbar-width:none] sm:[&::-webkit-scrollbar]:hidden"
        role="tablist"
        aria-label="Maç durumu"
      >
        {tabs.map((t) => {
          const active = stage === t.key
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setStage(t.key)}
              className={
                active
                  ? 'livescore-solid-red min-w-0 shrink-0 rounded-lg bg-[#D92E2E] px-2 py-2 text-xs font-bold text-white shadow-sm sm:px-3.5 sm:py-1.5 sm:text-sm'
                  : 'min-w-0 shrink-0 rounded-lg border border-gray-200 bg-white px-2 py-2 text-xs font-semibold text-gray-600 transition-colors hover:border-red-200 hover:bg-red-50/50 hover:text-[#D92E2E] dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-red-950/20 sm:px-3.5 sm:py-1.5 sm:text-sm'
              }
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {!visibleSections.length ? (
        <div className="rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
          Bu sekmede maç bulunamadı.
        </div>
      ) : (
        <div className="space-y-3">
          {visibleSections.map((sec) => (
            <section
              key={sec.id}
              className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 dark:shadow-none"
            >
              <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50/80 px-4 py-2.5 dark:border-slate-800 dark:bg-slate-950/40">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={leagueLogoSrc(sec.slug)}
                  alt=""
                  width={20}
                  height={20}
                  className="size-5 object-contain"
                />
                <h3 className="text-sm font-black text-gray-800 dark:text-slate-100">
                  {sec.title}
                </h3>
                <span className="livescore-match-count text-[11px] font-semibold text-gray-400">
                  {sec.matches.length}
                </span>
                <Link
                  href={matchesPath(sec.slug, day)}
                  className="ml-auto text-[11px] font-semibold text-red-600 hover:underline"
                >
                  Lig
                </Link>
              </div>
              <div>
                {sec.matches.map((m) => (
                  <LiveRow key={m.id} match={m} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  )
}

function LiveRow({ match }: { match: LivescoreMatch }) {
  const badge = badgeFor(match)
  const sub = subText(match)
  const hasScore = match.score.home != null && match.score.away != null

  return (
    <div className="border-b border-gray-100 px-3 py-3 transition-colors last:border-b-0 hover:bg-gray-50/70 dark:border-slate-800/80 dark:hover:bg-slate-800/30 sm:px-4">
      <div className="sm:hidden">
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-black tabular-nums text-gray-800 dark:text-slate-100">
            {match.start || '—'}
          </span>
          {badge ? (
            <span
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badge.className}`}
            >
              {match.stage === 'live' ? (
                <span
                  aria-hidden="true"
                  className="size-1.5 animate-pulse rounded-full bg-red-600"
                />
              ) : null}
              {badge.text}
            </span>
          ) : null}
          {sub ? (
            <span className="ml-auto truncate text-[10px] font-semibold text-gray-400">
              {sub}
            </span>
          ) : null}
        </div>

        <div className="mt-2 space-y-1.5">
          <MobileLiveTeam
            name={match.home.name}
            logo={match.home.logo}
            score={match.score.home}
            live={match.stage === 'live'}
            redCards={match.homeRedCards}
          />
          <MobileLiveTeam
            name={match.away.name}
            logo={match.away.logo}
            score={match.score.away}
            live={match.stage === 'live'}
            redCards={match.awayRedCards}
          />
        </div>
      </div>

      <div className="hidden grid-cols-[72px_1fr] items-center gap-2 sm:grid">
        <div className="flex flex-col items-start gap-1">
          <div className="text-sm font-bold tabular-nums text-gray-800 dark:text-slate-100">
            {match.start || '—'}
          </div>
          {badge ? (
            <span
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${badge.className}`}
            >
              {match.stage === 'live' ? (
                <span
                  aria-hidden="true"
                  className="size-1.5 animate-pulse rounded-full bg-red-600"
                />
              ) : null}
              {badge.text}
            </span>
          ) : null}
        </div>

        <div className="grid min-w-0 grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="flex min-w-0 items-center justify-end gap-2">
            <div className="truncate text-right text-sm font-semibold text-gray-900 dark:text-white">
              {match.home.name}
            </div>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={match.home.logo}
              alt=""
              width={24}
              height={24}
              className="size-6 shrink-0 object-contain"
              loading="lazy"
            />
          </div>

          <div className="flex min-w-[64px] flex-col items-center justify-center">
            {hasScore ? (
              <div className="rounded-md bg-gray-100 px-2.5 py-1 text-sm font-black tabular-nums text-gray-900 dark:bg-slate-800 dark:text-white">
                {match.score.home} - {match.score.away}
              </div>
            ) : (
              <div className="rounded-md bg-gray-50 px-2.5 py-1 text-xs font-bold uppercase text-gray-400 dark:bg-slate-800/60 dark:text-slate-500">
                vs
              </div>
            )}
            {sub ? (
              <div className="mt-0.5 text-[10px] font-medium text-gray-400">
                {sub}
              </div>
            ) : null}
          </div>

          <div className="flex min-w-0 items-center justify-start gap-2">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={match.away.logo}
              alt=""
              width={24}
              height={24}
              className="size-6 shrink-0 object-contain"
              loading="lazy"
            />
            <div className="truncate text-sm font-semibold text-gray-900 dark:text-white">
              {match.away.name}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function MobileLiveTeam({
  name,
  logo,
  score,
  live,
  redCards,
}: {
  name: string
  logo: string
  score: number | null
  live: boolean
  redCards: number
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <TeamLogo src={logo} alt={name} size={24} />
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900 dark:text-white">
        {name}
      </span>
      {redCards > 0 ? (
        <span
          className="livescore-solid-red inline-flex h-4 min-w-3 items-center justify-center rounded-sm bg-red-600 px-0.5 text-[9px] font-black text-white"
          title={`${redCards} kırmızı kart`}
        >
          {redCards}
        </span>
      ) : null}
      <span
        className={`flex size-7 shrink-0 items-center justify-center rounded-md text-sm font-black tabular-nums ${
          live
            ? 'livescore-solid-red bg-red-600 text-white'
            : 'bg-gray-100 text-gray-900 dark:bg-slate-800 dark:text-white'
        }`}
      >
        {score ?? '—'}
      </span>
    </div>
  )
}
